import React, { useState, useEffect, useCallback, useMemo } from "react";
import AppShell from "../layouts/AppShell";
import { GET, loading, format_date, Select } from "../components/CustomComponent";

/* ---------------------------------- style --------------------------------- */

const glassCardStyle = {
  borderRadius: 24,
  background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(238,247,252,0.92) 100%)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 18px 40px rgba(11, 90, 167, 0.10)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  // ยกทั้งแถบตัวกรองขึ้นเหนือการ์ดด้านล่าง ไม่งั้น dropdown ของ Select จะถูกบัง
  position: "relative",
  zIndex: 20,
};

// dropdown ของ Select กว้างอย่างน้อย 150px จึงล้นออกนอกคอลัมน์ตัวเองบนจอแคบ
// ไล่ z-index จากซ้ายไปขวาให้ตัวที่เปิดอยู่ทับตัวถัดไปเสมอ
const filterColStyle = (order) => ({ position: "relative", zIndex: 10 - order });

const heroCardStyle = {
  borderRadius: 24,
  border: "1px solid rgba(255,255,255,0.55)",
  boxShadow: "0 20px 40px rgba(12, 60, 120, 0.14)",
  position: "relative",
  overflow: "hidden",
};

const panelStyle = {
  borderRadius: 26,
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.12)",
  boxShadow: "0 16px 38px rgba(12, 118, 229, 0.08)",
};

const inputStyle = {
  borderRadius: 14,
  border: "1px solid #cfdce8",
  background: "#ffffff",
  color: "#1b3950",
  boxShadow: "none",
};

const labelStyle = {
  color: "#0b4f84",
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
};

const numberStyle = { fontVariantNumeric: "tabular-nums" };

const INCOME_COLOR = "#188251";
const EXPENSE_COLOR = "#b63636";
const BRAND_GRADIENT = "linear-gradient(135deg, #0f6bdc 0%, #1e89e4 48%, #27d6dd 100%)";

const THAI_MONTHS_SHORT = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

/* --------------------------------- options -------------------------------- */

const transactionTypeOptions = [
  { value: "expense", label: "เบิกจ่ายให้พนักงาน" },
];

const statusOptions = [
  { value: "success", label: "สำเร็จ" },
  { value: "pending", label: "รอดำเนินการ" },
  { value: "failed", label: "ไม่สำเร็จ" },
  { value: "cancelled", label: "ยกเลิก" },
];

const sortOptions = [
  { value: "code", label: "รหัสพนักงาน" },
  { value: "amount", label: "ยอดสูงสุด" },
  { value: "count", label: "จำนวนรายการมากสุด" },
  { value: "recent", label: "ทำรายการล่าสุด" },
  { value: "name", label: "ชื่อพนักงาน (ก-ฮ)" },
];

const DEFAULT_SORT = "code";

const defaultSummary = {
  total_count: 0,
  total_income: 0,
  total_income_net: 0,
  total_expense: 0,
  total_expense_net: 0,
  total_vat: 0,
  total_wht: 0,
  total_net: 0,
  balance: 0,
  employee_count: 0,
};

/* -------------------------------- utilities ------------------------------- */

const formatMoney = (value) => {
  const num = Number(String(value ?? "").replace(/,/g, ""));
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCompact = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(num / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return num.toFixed(0);
};

// วงเงินเบิกค่าใช้จ่ายต่อเดือน: 0 = ไม่จำกัด (ตั้งค่าที่หน้า Settings > วงเงินเบิกค่าใช้จ่าย)
const formatLimitText = (value) => (Number(value) > 0 ? `฿ ${formatMoney(value)}` : "ไม่จำกัด");

const getEmployeeName = (employee) => {
  if (!employee) return "-";
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return fullName || employee.nickname || employee.employee_code || "-";
};

const getInitials = (employee) =>
  getEmployeeName(employee)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "-";

const hasPicture = (picture) => typeof picture === "string" && /^(data:|https?:\/\/)/.test(picture.trim());

// เรียงตามรหัสพนักงานแบบ natural (EMP2 มาก่อน EMP10) คนที่ยังไม่มีรหัสไปต่อท้าย
const compareEmployeeCode = (a, b) => {
  const codeA = String(a.employee?.employee_code || "").trim();
  const codeB = String(b.employee?.employee_code || "").trim();
  if (!codeA && !codeB) return getEmployeeName(a.employee).localeCompare(getEmployeeName(b.employee), "th");
  if (!codeA) return 1;
  if (!codeB) return -1;
  return codeA.localeCompare(codeB, "th", { numeric: true, sensitivity: "base" });
};

const getStatusBadgeStyle = (status) => {
  if (status === "success") return { background: "rgba(47, 181, 114, 0.14)", color: INCOME_COLOR };
  if (status === "pending") return { background: "rgba(245, 166, 35, 0.16)", color: "#b97007" };
  if (status === "failed" || status === "cancelled") return { background: "rgba(229, 83, 83, 0.16)", color: EXPENSE_COLOR };
  return { background: "rgba(108, 122, 137, 0.14)", color: "#566575" };
};

const getStatusLabel = (status) => statusOptions.find((item) => item.value === status)?.label || status || "-";

/* ------------------------------- sub-components ---------------------------- */

const Avatar = ({ employee, size = 46, fontSize = "0.95rem" }) => {
  const style = {
    width: size,
    height: size,
    borderRadius: size / 2.6,
    border: "1px solid",
    flexShrink: 0,
    objectFit: "cover",
    background: BRAND_GRADIENT,
    boxShadow: "0 8px 18px rgba(15, 107, 220, 0.22)",
  };

  if (hasPicture(employee?.picture)) {
    return <img src={employee.picture} alt={getEmployeeName(employee)} style={style} />;
  }

  return (
    <span className="text-white d-flex align-items-center justify-content-center fw-bold" style={{ ...style, fontSize }}>
      {getInitials(employee)}
    </span>
  );
};

const StatPill = ({ label, value, color, light = false }) => (
  <div
    className="px-3 py-2"
    style={{
      borderRadius: 16,
      background: light ? "rgba(255,255,255,0.18)" : "rgba(15, 53, 81, 0.04)",
      border: light ? "1px solid" : "1px solid rgba(15, 53, 81, 0.06)",
      minWidth: 132,
    }}
  >
    <div className="small mb-1" style={{ color: light ? "rgba(255,255,255,0.82)" : "#6e8c9c", fontSize: "0.72rem" }}>
      {label}
    </div>
    <div className="fw-bold" style={{ ...numberStyle, color: light ? "#ffffff" : color, fontSize: "1.02rem" }}>
      {value}
    </div>
  </div>
);

/* ---------------------------------- page ---------------------------------- */

export default function EmployeeFinance() {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(String(currentYear));
  const [transactionType, setTransactionType] = useState("");
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const [employees, setEmployees] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");
  const [detail, setDetail] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState([]);

  const yearOptions = useMemo(() => {
    const options = [];
    for (let value = currentYear; value >= currentYear - 4; value -= 1) {
      options.push({ value: String(value), label: `ปี ${value}` });
    }
    return options;
  }, [currentYear]);

  const filterBody = useMemo(
    () => ({
      year,
      transaction_type: transactionType,
      status,
      data_search: search.trim(),
    }),
    [year, transactionType, status, search]
  );

  const getOverview = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/employee-finance", filterBody);
    if (result?.status) {
      setEmployees(result.data?.employees || []);
    }
    loading(false);
  }, [filterBody]);

  const getDetail = useCallback(async () => {
    if (!selectedUid) {
      setDetail(null);
      return;
    }
    loading(true);
    const result = await GET(`v1/admin/employee-finance/${selectedUid}`, filterBody);
    if (result?.status) {
      setDetail(result.data || null);
      // เริ่มต้นย่อทุกเดือนไว้ก่อน ให้ผู้ใช้กดเปิดเดือนที่ต้องการเอง
      setExpandedMonths([]);
    }
    loading(false);
  }, [selectedUid, filterBody]);

  useEffect(() => {
    getOverview();
  }, [getOverview]);

  useEffect(() => {
    getDetail();
  }, [getDetail]);

  // เลือกพนักงานคนแรกให้อัตโนมัติเมื่อรายการเปลี่ยน
  useEffect(() => {
    if (!employees.length) {
      setSelectedUid("");
      return;
    }
    setSelectedUid((current) => (employees.some((item) => item.employee?.uid === current) ? current : employees[0].employee?.uid || ""));
  }, [employees]);

  const handleSearch = () => setSearch(searchInput);

  const handleResetFilters = () => {
    setSearchInput("");
    setSearch("");
    setTransactionType("");
    setStatus("");
    setYear(String(currentYear));
    setSortBy(DEFAULT_SORT);
  };

  const sortedEmployees = useMemo(() => {
    const list = [...employees];
    if (sortBy === "amount") return list.sort((a, b) => b.total_net - a.total_net);
    if (sortBy === "count") return list.sort((a, b) => b.total_count - a.total_count);
    if (sortBy === "recent") return list.sort((a, b) => String(b.last_transaction_date || "").localeCompare(String(a.last_transaction_date || "")));
    if (sortBy === "name") return list.sort((a, b) => getEmployeeName(a.employee).localeCompare(getEmployeeName(b.employee), "th"));
    return list.sort(compareEmployeeCode);
  }, [employees, sortBy]);

  const activeEmployeeCount = useMemo(() => employees.filter((item) => Number(item.total_count) > 0).length, [employees]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths((current) => (current.includes(monthKey) ? current.filter((key) => key !== monthKey) : [...current, monthKey]));
  };

  const allExpanded = detail?.months?.length > 0 && expandedMonths.length === detail.months.length;

  const toggleAllMonths = () => {
    setExpandedMonths(allExpanded ? [] : (detail?.months || []).map((month) => month.month_key));
  };

  const renderEmployeeCard = (item) => {
    const employee = item.employee;
    const isActive = employee?.uid === selectedUid;
    // พนักงานที่ยังไม่มีธุรกรรมในเงื่อนไขนี้ แสดงยอด 0 แบบจางลงให้กวาดสายตาง่าย
    const isEmpty = !Number(item.total_count);

    return (
      <button
        key={employee?.uid}
        type="button"
        onClick={() => setSelectedUid(employee?.uid || "")}
        className="w-100 text-start border-0 p-3 mb-2"
        style={{
          borderRadius: 20,
          // คนที่เลือกอยู่: พื้นหลังขาวเหมือนกันทุกใบ แยกด้วยกรอบสีฟ้าตามธีมแทน จะได้ไม่กลืนกับพื้นหลัง
          background: "#ffffff",
          color: "#12314d",
          boxShadow: isActive
            ? "0 14px 28px rgba(15, 107, 220, 0.18), inset 0 0 0 2px #0f6bdc"
            : "inset 0 0 0 1px rgba(15, 53, 81, 0.07)",
          transition: "transform 180ms ease, box-shadow 180ms ease",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <Avatar employee={employee} />
          <div className="flex-grow-1 overflow-hidden">
            <div className="fw-bold text-truncate" style={{ fontSize: "0.95rem" }}>
              {getEmployeeName(employee)}
            </div>
            <div className="small text-truncate" style={{ color: "#6e8c9c", fontSize: "0.76rem" }}>
              {employee?.employee_code ? `${employee.employee_code} · ` : ""}
              {employee?.position || employee?.department || "ไม่ระบุตำแหน่ง"}
            </div>
            {Number(item.over_limit_month_count) > 0 ? (
              <span
                className="d-inline-block rounded-pill px-2 py-1 mt-1"
                style={{
                  background: "rgba(229, 83, 83, 0.14)",
                  color: EXPENSE_COLOR,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                }}
                title={`เบิกเกินวงเงิน ${formatLimitText(item.expense_monthly_limit)} ต่อเดือน`}
              >
                <i className="fa-solid fa-triangle-exclamation me-1" />
                เกินวงเงิน {item.over_limit_month_count} เดือน
              </span>
            ) : null}
          </div>
          <div className="text-end flex-shrink-0">
            <div
              className="fw-bold"
              style={{ ...numberStyle, color: isActive ? "#0b4f84" : "#12314d", fontSize: "0.95rem", opacity: isEmpty && !isActive ? 0.42 : 1 }}
            >
              ฿ {formatCompact(item.total_net)}
            </div>
            <div className="small" style={{ color: "#6e8c9c", fontSize: "0.72rem" }}>
              {item.total_count} รายการ
            </div>
          </div>
        </div>
      </button>
    );
  };

  const renderTransactionRow = (transaction) => {
    const isIncome = transaction.transaction_type === "income";
    const day = String(transaction.transaction_date || "").slice(8, 10) || "--";
    const monthIndex = Number(String(transaction.transaction_date || "").slice(5, 7)) - 1;

    return (
      <div
        key={transaction.uid}
        className="d-flex align-items-center gap-3 px-3 py-3 empfin-tx-row"
        style={{ borderTop: "1px solid rgba(15, 53, 81, 0.06)" }}
      >
        <div
          className="text-center flex-shrink-0"
          style={{
            width: 48,
            padding: "6px 0",
            borderRadius: 14,
            background: "linear-gradient(160deg, rgba(15,107,220,0.08) 0%, rgba(39,214,221,0.14) 100%)",
            color: "#0b4f84",
          }}
        >
          <div className="fw-bold" style={{ ...numberStyle, fontSize: "1.02rem", lineHeight: 1.1 }}>
            {day}
          </div>
          <div style={{ fontSize: "0.66rem", opacity: 0.8 }}>{THAI_MONTHS_SHORT[monthIndex] || "-"}</div>
        </div>

        <div className="flex-grow-1 overflow-hidden">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              className="fw-semibold text-truncate"
              style={{ color: "#12314d", fontSize: "0.92rem", minWidth: 0 }}
              title={transaction.description || transaction.category?.category_name || "ไม่ระบุรายละเอียด"}
            >
              {transaction.description || transaction.category?.category_name || "ไม่ระบุรายละเอียด"}
            </span>
            {transaction.category?.category_name ? (
              <span
                className="rounded-pill px-2 py-1 flex-shrink-0 text-nowrap"
                style={{ background: "rgba(15, 107, 220, 0.09)", color: "#0b4f84", fontSize: "0.68rem", fontWeight: 700 }}
              >
                {transaction.category.category_name}
              </span>
            ) : null}
            <span
              className="rounded-pill px-2 py-1 flex-shrink-0 text-nowrap"
              style={{ ...getStatusBadgeStyle(transaction.status), fontSize: "0.68rem", fontWeight: 700 }}
            >
              {getStatusLabel(transaction.status)}
            </span>
          </div>
          <div className="small text-truncate mt-1" style={{ color: "#7d94a6", fontSize: "0.74rem" }}>
            {transaction.transaction_code || "-"}
            {transaction.document?.doc_no ? ` · เอกสาร ${transaction.document.doc_no}` : ""}
            {transaction.reference_no ? ` · อ้างอิง ${transaction.reference_no}` : ""}
            {transaction.transaction_date ? ` · ${format_date(transaction.transaction_date, "dd/mm/yyyy hh:mm")}` : ""}
          </div>
        </div>

        <div className="text-end flex-shrink-0 ps-3 ps-md-4 empfin-tx-amount" style={{ minWidth: 150 }}>
          <div className="fw-bold" style={{ ...numberStyle, color: isIncome ? INCOME_COLOR : EXPENSE_COLOR, fontSize: "1rem" }}>
            {isIncome ? "+" : "-"} {formatMoney(transaction.amount_net || transaction.amount)}
          </div>
          {Number(transaction.vat) || Number(transaction.wht) ? (
            <div className="small" style={{ ...numberStyle, color: "#7d94a6", fontSize: "0.7rem" }}>
              {Number(transaction.vat) ? `VAT ${formatMoney(transaction.vat)}` : ""}
              {Number(transaction.vat) && Number(transaction.wht) ? " · " : ""}
              {Number(transaction.wht) ? `หัก ณ ที่จ่าย ${formatMoney(transaction.wht)}` : ""}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderMonthCard = (month) => {
    const isOpen = expandedMonths.includes(month.month_key);
    const limit = Number(month.expense_limit) || 0;
    const used = Number(month.expense_limit_used ?? month.total_expense_net) || 0;
    const percent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
    const isOverLimit = !!month.is_over_limit;
    // ใกล้เต็มวงเงิน (ตั้งแต่ 80%) เตือนสีส้มไว้ก่อน
    const isNearLimit = !isOverLimit && limit > 0 && used / limit >= 0.8;
    const limitColor = isOverLimit ? EXPENSE_COLOR : isNearLimit ? "#b97007" : INCOME_COLOR;

    return (
      <div
        key={month.month_key}
        className="mb-3"
        style={{
          ...panelStyle,
          overflow: "hidden",
          // เดือนที่กดเปิดอยู่: พื้นหลังขาวเหมือนเดิม แต่ตีกรอบสีฟ้าตามธีมให้เห็นชัดว่าเลือกอันไหนอยู่
          border: isOpen ? "1px solid #0f6bdc" : panelStyle.border,
          transition: "border-color 200ms ease, box-shadow 200ms ease",
        }}
      >
        <button
          type="button"
          onClick={() => toggleMonth(month.month_key)}
          className="w-100 border-0 text-start d-flex align-items-center gap-3 px-3 px-md-4 py-3 empfin-month-header"
          style={{ background: "#ffffff" }}
        >
          <span
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: BRAND_GRADIENT,
              color: "#ffffff",
              boxShadow: "0 10px 20px rgba(15, 107, 220, 0.20)",
              fontWeight: 800,
              fontSize: "0.8rem",
            }}
          >
            {month.month_short_label}
          </span>

          <div className="flex-grow-1 overflow-hidden">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-bold text-truncate" style={{ color: "#12314d", fontSize: "1.02rem" }}>
                {month.month_label}
              </span>
              {isOverLimit ? (
                <span
                  className="rounded-pill px-2 py-1 flex-shrink-0 text-nowrap"
                  style={{ background: "rgba(229, 83, 83, 0.14)", color: EXPENSE_COLOR, fontSize: "0.68rem", fontWeight: 700 }}
                >
                  <i className="fa-solid fa-triangle-exclamation me-1" />
                  เกินวงเงิน {formatMoney(month.over_limit_amount)}
                </span>
              ) : null}
            </div>
            <div className="small" style={{ color: "#7d94a6", fontSize: "0.76rem" }}>
              {month.total_count} รายการ
              {limit > 0 ? ` · ใช้ไป ${formatMoney(used)} / ${formatMoney(limit)} (${Math.round((used / limit) * 100)}%)` : " · ไม่จำกัดวงเงิน"}
            </div>
            {limit > 0 ? (
              <div className="mt-2" style={{ height: 6, borderRadius: 999, background: "rgba(15, 53, 81, 0.08)", maxWidth: 320 }}>
                <div style={{ width: `${percent}%`, height: "100%", borderRadius: 999, background: limitColor, transition: "width 220ms ease" }} />
              </div>
            ) : null}
          </div>

          <div className="d-none d-md-flex align-items-center gap-4 flex-shrink-0">
            <div className="text-end">
              <div className="small" style={{ color: "#7d94a6", fontSize: "0.7rem" }}>
                เบิกจ่าย
              </div>
              <div className="fw-bold" style={{ ...numberStyle, color: EXPENSE_COLOR, fontSize: "0.95rem" }}>
                {formatMoney(month.total_expense_net)}
              </div>
            </div>
          </div>

          <div className="text-end flex-shrink-0 empfin-month-total" style={{ minWidth: 110 }}>
            <div className="small" style={{ color: "#7d94a6", fontSize: "0.7rem" }}>
              ยอดรวมเดือนนี้
            </div>
            <div className="fw-bold" style={{ ...numberStyle, color: "#0b4f84", fontSize: "1.02rem" }}>
              ฿ {formatMoney(month.total_net)}
            </div>
          </div>

          <i
            className="fa-solid fa-chevron-down flex-shrink-0"
            style={{
              color: "#0b4f84",
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 200ms ease",
              fontSize: "0.85rem",
            }}
          />
        </button>

        {isOpen ? <div>{(month.transactions || []).map(renderTransactionRow)}</div> : null}
      </div>
    );
  };

  const selectedEmployee = detail?.employee;
  const detailSummary = detail?.summary || defaultSummary;

  return (
    <AppShell
      title="ธุรกรรมการเงินพนักงาน"
      description="ยอดธุรกรรมการเงินของพนักงานแต่ละคน แยกตามเดือน (ไม่รวมเงินเดือนและค่าใช้จ่ายบริษัท)"
    >
      {/* ------------------------------ filter bar ------------------------------ */}
      <div className="p-3 p-md-4 mb-4" style={glassCardStyle}>
        <div className="row g-3 align-items-end">
          <div className="col-12 col-sm-6 col-md-3 col-xl-2" style={filterColStyle(0)}>
            <div style={labelStyle}>ปี</div>
            <Select className="form-control" value={year} options={yearOptions} placeholder="เลือกปี" onChange={(value) => setYear(value)} />
          </div>
          <div className="col-12 col-sm-6 col-md-3 col-xl-2" style={filterColStyle(2)}>
            <div style={labelStyle}>สถานะ</div>
            <Select className="form-control" value={status} options={statusOptions} placeholder="ทุกสถานะ" onChange={(value) => setStatus(value)} />
          </div>
          <div className="col-12 col-sm-6 col-md-3 col-xl-2" style={filterColStyle(3)}>
            <div style={labelStyle}>เรียงลำดับ</div>
            <Select className="form-control" value={sortBy} options={sortOptions} placeholder="เรียงลำดับ" onChange={(value) => setSortBy(value || DEFAULT_SORT)} />
          </div>
          <div className="col-12 col-xl-4" style={filterColStyle(4)}>
            <div style={labelStyle}>ค้นหา</div>
            <div className="d-flex gap-2 filters-mobile-stack">
              <input
                className="form-control"
                style={inputStyle}
                placeholder="รหัสธุรกรรม / รายละเอียด / เลขอ้างอิง"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
              <button
                type="button"
                className="btn text-white px-3 flex-shrink-0"
                style={{ borderRadius: 14, background: BRAND_GRADIENT, boxShadow: "0 10px 20px rgba(15,107,220,0.22)" }}
                onClick={handleSearch}
              >
                <i className="fa-solid fa-magnifying-glass" />
              </button>
              <button
                type="button"
                className="btn px-3 flex-shrink-0"
                style={{ borderRadius: 14, background: "rgba(15,53,81,0.06)", color: "#0b4f84" }}
                onClick={handleResetFilters}
                title="ล้างตัวกรอง"
              >
                <i className="fa-solid fa-rotate-left" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------ main content ---------------------------- */}
      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="p-3 p-md-4 h-100" style={panelStyle}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h6 className="fw-bold mb-1" style={{ color: "#12314d" }}>
                  พนักงาน
                </h6>
                <div className="small" style={{ color: "#7d94a6", fontSize: "0.76rem" }}>
                  ทั้งหมด {sortedEmployees.length} คน · มีธุรกรรมในปี {year} {activeEmployeeCount} คน
                </div>
              </div>
            </div>

            <div style={{ maxHeight: 720, overflowY: "auto", paddingRight: 4 }}>
              {sortedEmployees.length > 0 ? (
                sortedEmployees.map(renderEmployeeCard)
              ) : (
                <div className="text-center py-5" style={{ color: "#7d94a6" }}>
                  <i className="fa-solid fa-user-slash mb-3" style={{ fontSize: "1.6rem", opacity: 0.5 }} />
                  <div className="small">ยังไม่มีข้อมูลพนักงานในระบบ</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          {selectedEmployee ? (
            <>
              <div
                className="p-3 p-md-4 mb-4 text-white position-relative"
                style={{ ...heroCardStyle, background: BRAND_GRADIENT, boxShadow: "0 24px 46px rgba(15, 107, 220, 0.26)" }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: -60,
                    right: -40,
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.10)",
                  }}
                />
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 position-relative">
                  <Avatar employee={selectedEmployee} size={64} fontSize="1.35rem" />
                  <div className="flex-grow-1 overflow-hidden">
                    <h4 className="fw-bold mb-1 text-truncate">{getEmployeeName(selectedEmployee)}</h4>
                    <div className="small" style={{ opacity: 0.86 }}>
                      {selectedEmployee.employee_code ? `${selectedEmployee.employee_code} · ` : ""}
                      {selectedEmployee.position || "ไม่ระบุตำแหน่ง"}
                      {selectedEmployee.department ? ` · ${selectedEmployee.department}` : ""}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <StatPill label="เบิกจ่าย" value={`฿ ${formatMoney(detailSummary.total_expense_net)}`} light />
                  </div>
                </div>
              </div>

              {Number(detail?.over_limit_month_count) > 0 ? (
                <div
                  className="d-flex align-items-center gap-3 px-3 py-3 mb-4"
                  style={{ borderRadius: 18, background: "rgba(229, 83, 83, 0.10)", border: "1px solid rgba(229, 83, 83, 0.20)" }}
                >
                  <i className="fa-solid fa-triangle-exclamation flex-shrink-0" style={{ color: EXPENSE_COLOR, fontSize: "1.1rem" }} />
                  <div>
                    <div className="fw-bold" style={{ color: EXPENSE_COLOR, fontSize: "0.92rem" }}>
                      เบิกเกินวงเงิน {detail.over_limit_month_count} เดือน
                    </div>
                    <div className="small" style={{ color: "#7d5a5a", fontSize: "0.76rem" }}>
                      วงเงินที่ใช้อยู่ {formatLimitText(detail?.expense_monthly_limit)} ต่อเดือน · แก้ไขได้ที่ ตั้งค่า &gt; วงเงินเบิกค่าใช้จ่าย
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h6 className="fw-bold mb-1" style={{ color: "#12314d" }}>
                    รายการแยกตามเดือน
                  </h6>
                  <div className="small" style={{ color: "#7d94a6", fontSize: "0.76rem" }}>
                    {detail?.months?.length || 0} เดือน · {detailSummary.total_count} รายการ
                  </div>
                </div>
                {detail?.months?.length ? (
                  <button
                    type="button"
                    className="btn btn-sm px-3"
                    style={{ borderRadius: 12, background: "rgba(15,53,81,0.06)", color: "#0b4f84", fontWeight: 600 }}
                    onClick={toggleAllMonths}
                  >
                    {allExpanded ? "ย่อทั้งหมด" : "ขยายทั้งหมด"}
                  </button>
                ) : null}
              </div>

              {detail?.months?.length ? (
                detail.months.map(renderMonthCard)
              ) : (
                <div className="text-center py-5" style={{ ...panelStyle, color: "#7d94a6" }}>
                  <i className="fa-solid fa-receipt mb-3" style={{ fontSize: "1.8rem", opacity: 0.5 }} />
                  <div className="small">ยังไม่มีธุรกรรมของพนักงานคนนี้ในเงื่อนไขที่เลือก</div>
                </div>
              )}
            </>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center text-center p-5 h-100" style={panelStyle}>
              <i className="fa-solid fa-hand-holding-dollar mb-3" style={{ fontSize: "2rem", color: "#0f6bdc", opacity: 0.4 }} />
              <h6 className="fw-bold mb-1" style={{ color: "#12314d" }}>
                เลือกพนักงานเพื่อดูรายละเอียด
              </h6>
              <div className="small" style={{ color: "#7d94a6" }}>
                ระบบจะแสดงธุรกรรมการเงินทั้งหมดของพนักงาน แยกกลุ่มตามเดือน
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
